/* iter-50h — QA / Regression panel.
 *
 * One-shot "Run all tests" badge for /admin. Lists every pytest module
 * under /app/backend/tests with a green/red pulse + last-run summary, a
 * per-module "Run" button, and a top-level "Run all" button.
 *
 * The backend (`/api/admin/qa/run`) shells out to pytest, parses the
 * `N passed, M failed in t.ts` summary, and returns counts so this panel
 * doesn't have to re-implement the parser. Results live only in
 * component state (no DB writes) — each visit starts fresh.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, Loader2, PlayCircle, AlertTriangle, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

export default function QAPanel() {
  const [modules, setModules]   = useState(null);   // null=loading, []=loaded
  const [enabled, setEnabled]   = useState(true);
  const [results, setResults]   = useState({});     // filename -> { ok, counts, duration_s, timed_out, output_tail }
  const [running, setRunning]   = useState({});     // filename or '__all__' -> bool
  const [openTail, setOpenTail] = useState(null);   // currently expanded module

  const loadModules = useCallback(async () => {
    try {
      const { data } = await http.get('/admin/qa/modules');
      setModules(data.modules || []);
      setEnabled(data.enabled !== false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Failed to load modules');
      setModules([]);
    }
  }, []);

  useEffect(() => { loadModules(); }, [loadModules]);

  const runOne = useCallback(async (filename) => {
    setRunning((r) => ({ ...r, [filename]: true }));
    try {
      const { data } = await http.post('/admin/qa/run', { module: filename }, { timeout: 100000 });
      setResults((res) => ({ ...res, [filename]: { ...data, ran_at: new Date().toISOString() } }));
      if (data.ok) toast.success(`${filename} · ${data.counts?.passed ?? 0} passed`);
      else toast.error(`${filename} · ${data.counts?.failed ?? 0} failed`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (err.message || 'Run failed'));
    } finally {
      setRunning((r) => { const n = { ...r }; delete n[filename]; return n; });
    }
  }, []);

  const runAll = useCallback(async () => {
    setRunning((r) => ({ ...r, __all__: true }));
    setResults({});
    setOpenTail(null);
    try {
      // Sequential per-module so one slow test doesn't hide others;
      // also keeps the toasts and progress legible.
      for (const m of modules) {
        await runOne(m.filename);
      }
      toast.success('Run-all complete.');
    } finally {
      setRunning((r) => { const n = { ...r }; delete n.__all__; return n; });
    }
  }, [modules, runOne]);

  const totals = useMemo(() => {
    const t = { passed: 0, failed: 0, error: 0, skipped: 0, ran: 0 };
    Object.values(results).forEach((r) => {
      t.passed += r.counts?.passed || 0;
      t.failed += r.counts?.failed || 0;
      t.error  += r.counts?.error  || 0;
      t.skipped += r.counts?.skipped || 0;
      t.ran    += 1;
    });
    return t;
  }, [results]);

  if (modules === null) {
    return (
      <div className="flex items-center gap-2 text-[12.5px] text-muted-foreground" data-testid="qa-panel-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading test modules…
      </div>
    );
  }

  return (
    <div data-testid="qa-panel" className="space-y-4">
      {!enabled && (
        <div className="rounded-md border border-amber-300/70 bg-amber-50 px-3 py-2 text-[12px] text-amber-800" data-testid="qa-disabled-banner">
          <strong>QA runner disabled in this environment.</strong> The test
          suite mutates live data, so it must be enabled per-environment.
          Set <code className="font-mono-stat">QA_RUNNER_ENABLED=1</code> in
          <code className="font-mono-stat"> backend/.env</code> to enable here.
        </div>
      )}
      <div className="rounded-md border border-border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-[15px] font-semibold tracking-tight">Backend regression suite</h2>
            <p className="mt-0.5 text-[11.5px] text-muted-foreground">
              {modules.length} test modules under <code className="font-mono-stat">/app/backend/tests/</code>.
              Each run shells out to <code className="font-mono-stat">pytest</code> and parses the summary line.
            </p>
          </div>
          <Button
            onClick={runAll}
            disabled={!enabled || !!running.__all__ || Object.keys(running).length > 0}
            data-testid="qa-run-all-btn"
            className="gap-2"
          >
            {running.__all__
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <PlayCircle className="h-4 w-4" />}
            Run all
          </Button>
        </div>

        {totals.ran > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-[11.5px]" data-testid="qa-totals">
            <span className="rounded-sm bg-emerald-50 px-2 py-1 font-semibold text-emerald-800">
              {totals.passed} passed
            </span>
            <span className={`rounded-sm px-2 py-1 font-semibold ${totals.failed ? 'bg-rose-50 text-rose-800' : 'bg-secondary text-muted-foreground'}`}>
              {totals.failed} failed
            </span>
            <span className={`rounded-sm px-2 py-1 font-semibold ${totals.error ? 'bg-amber-50 text-amber-800' : 'bg-secondary text-muted-foreground'}`}>
              {totals.error} errors
            </span>
            {totals.skipped > 0 && (
              <span className="rounded-sm bg-secondary px-2 py-1 text-muted-foreground">
                {totals.skipped} skipped
              </span>
            )}
            <span className="text-muted-foreground">across {totals.ran} module{totals.ran === 1 ? '' : 's'}</span>
          </div>
        )}
      </div>

      <div className="rounded-md border border-border bg-card">
        <table className="w-full text-[12.5px]">
          <thead className="bg-panel-header/40 text-[10.5px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">Module</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-right">Passed</th>
              <th className="px-3 py-2 text-right">Failed</th>
              <th className="px-3 py-2 text-right">Time</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {modules.map((m) => {
              const r = results[m.filename];
              const isRunning = !!running[m.filename] || !!running.__all__;
              const disableRun = isRunning || !enabled;
              return (
                <React.Fragment key={m.filename}>
                  <tr className="border-t border-border" data-testid={`qa-row-${m.filename}`}>
                    <td className="px-3 py-2">
                      <div className="font-mono-stat text-[11.5px] text-foreground">{m.filename}</div>
                      <div className="text-[10.5px] text-muted-foreground">{m.label}</div>
                    </td>
                    <td className="px-3 py-2">
                      <StatusPill result={r} running={isRunning && !running.__all__} />
                    </td>
                    <td className="px-3 py-2 text-right font-mono-stat text-emerald-700">{r?.counts?.passed ?? '—'}</td>
                    <td className={`px-3 py-2 text-right font-mono-stat ${r?.counts?.failed ? 'text-rose-700' : 'text-muted-foreground'}`}>
                      {r?.counts?.failed ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-right font-mono-stat text-muted-foreground">
                      {r ? `${r.duration_s}s` : '—'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={disableRun}
                          onClick={() => runOne(m.filename)}
                          data-testid={`qa-run-${m.filename}`}
                          className="h-7 gap-1 text-[11px]"
                        >
                          {running[m.filename]
                            ? <Loader2 className="h-3 w-3 animate-spin" />
                            : <PlayCircle className="h-3 w-3" />}
                          Run
                        </Button>
                        {r && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setOpenTail(openTail === m.filename ? null : m.filename)}
                            data-testid={`qa-tail-${m.filename}`}
                            className="h-7 text-[11px]"
                          >
                            {openTail === m.filename ? 'Hide' : 'Log'}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {openTail === m.filename && r?.output_tail && (
                    <tr className="border-t border-border bg-panel-header/20">
                      <td colSpan={6} className="px-3 py-2">
                        <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded-sm bg-background p-2 text-[10.5px] leading-snug text-muted-foreground" data-testid={`qa-tail-pre-${m.filename}`}>
{r.output_tail}
                        </pre>
                        {r.truncated && (
                          <div className="mt-1 text-[10.5px] text-amber-700">
                            Output truncated to last 16 KB.
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusPill({ result, running }) {
  if (running) {
    return (
      <span className="inline-flex items-center gap-1 rounded-sm bg-amber-50 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-amber-800">
        <Loader2 className="h-3 w-3 animate-spin" /> Running
      </span>
    );
  }
  if (!result) {
    return <span className="inline-flex items-center gap-1 rounded-sm bg-secondary px-2 py-0.5 text-[10.5px] uppercase tracking-wider text-muted-foreground"><Clock className="h-3 w-3" /> Pending</span>;
  }
  if (result.timed_out) {
    return <span className="inline-flex items-center gap-1 rounded-sm bg-amber-50 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-amber-800"><AlertTriangle className="h-3 w-3" /> Timed out</span>;
  }
  if (result.ok) {
    return <span className="inline-flex items-center gap-1 rounded-sm bg-emerald-50 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-emerald-700"><CheckCircle2 className="h-3 w-3" /> Pass</span>;
  }
  return <span className="inline-flex items-center gap-1 rounded-sm bg-rose-50 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-rose-700"><XCircle className="h-3 w-3" /> Fail</span>;
}
